import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Select, Tag, Space, Input } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import thLocale from '@fullcalendar/core/locales/th'; 
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel, faChalkboard, faSave, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { CSVLink } from 'react-csv';
import withRoleAccess from '../../hoc/withRoleAccess';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import CustomModal from './CustomModal';
import moment from 'moment-timezone';
import ClassDetailsModal from './ClassDetailsModal';
import { confirmAlert } from 'react-confirm-alert'; // Import the confirmation dialog
import 'react-confirm-alert/src/react-confirm-alert.css'; // Import the styles
import './Classes.css';

const { Option } = Select;

function AllClasses() {
  const { t, i18n } = useTranslation();
  const [classes, setClasses] = useState([]);
  const [classChangeLogs, setClassChangeLogs] = useState([]);
  const [teacherEvents, setTeacherEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageGroup, setCurrentPageGroup] = useState(0);
  const [selectedTeachers, setSelectedTeachers] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [editingClassroomId, setEditingClassroomId] = useState(null);
  const [editedClassroomId, setEditedClassroomId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [currentEventInfo, setCurrentEventInfo] = useState(null);
  const [changeReason, setChangeReason] = useState('');
  const [changeNote, setChangeNote] = useState('');
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [calendarView, setCalendarView] = useState('dayGridMonth');
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [hasInitialized, setHasInitialized] = useState(false);
  const calendarRef = useRef(null);

  const API_URL = process.env.REACT_APP_API_URL;


  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { day: '2-digit', month: 'short' };
    return new Intl.DateTimeFormat(i18n.language, options).format(date).toUpperCase();
  };

  const formatTime = (timeString) => {
    const [hours, minutes] = timeString.split(':');
    return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
  };

  const formatScheduleHour = (scheduleHourString) => {
    const [hours, minutes] = scheduleHourString.split(' ').map((value, index) => (index === 0 ? parseInt(value) : parseInt(value)));
    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  };


  const getDayOfWeek = (dateString) => {
    const date = new Date(dateString);
    const daysOfWeek = t('daysOfWeek', { returnObjects: true });
    return daysOfWeek[date.getDay()];
  };

  const parseScheduleHour = (scheduleHourString) => {
    const [hours, minutes] = scheduleHourString.split(' ').map((value, index) => (index === 0 ? parseInt(value) * 60 : parseInt(value)));
    return hours + minutes;
  };

  const tagRender = (props) => {
    const { label, value, closable, onClose } = props;
    const teacher = teachers.find(t => t.userid === value);
    
    return (
      <Tag
        color={teacher?.color || '#108ee9'}
        closable={closable}
        onClose={onClose}
        style={{ 
          marginRight: 3,
          display: 'flex',
          alignItems: 'center',
          padding: '4px 8px',
        }}
      >
        <Space size={4}>
          <UserOutlined style={{ fontSize: '12px' }} />
          {label}
        </Space>
      </Tag>
    );
  };

const [initialLoad, setInitialLoad] = useState(true);
const lastFetchedMonth = useRef(null);

const fetchClasses = useCallback(
  async (viewDate = new Date()) => {
    // Only show full-page loading on first load
    if (initialLoad) setLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setError("No token found");
        setLoading(false);
        return;
      }

      const dateStr = viewDate instanceof Date
        ? viewDate.toISOString().split("T")[0]
        : moment(viewDate).format("YYYY-MM-DD");

      console.log('[AllClasses] Fetching classes for viewDate:', dateStr);

      const response = await axios.get(`${API_URL}/classes/calendar-classes`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { viewDate: dateStr },
      });

      setClasses(response.data?.data || []);
      console.log('[AllClasses] Received', (response.data?.data || []).length, 'classes');
    } catch (error) {
      console.error("❌ Error fetching classes:", error);
      setError("Failed to fetch classes. Please ensure you are logged in.");
    } finally {
      setLoading(false);
      if (initialLoad) setInitialLoad(false);
    }
  },
  [API_URL, initialLoad]
);

   const fetchClassChangeLogs = useCallback(async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${API_URL}/class-change/class-change-logs`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    // Properly call the setter and update the state
    setClassChangeLogs(response.data);

  } catch (error) {
    console.error('❌ Error fetching class change logs:', error);
  }
}, [API_URL]);
 
  const fetchTeacherEvents = useCallback(async (teacherIds) => {
    if (teacherIds.length === 0) {
      setTeacherEvents([]);
      return;
    }
  
    try {
      const token = localStorage.getItem('token');
      const promises = teacherIds.map((teacherId) =>
        axios.get(`${API_URL}/teachers/calendar/${teacherId}/events-with-shared`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then(response => ({ 
          teacherId,  // Include teacherId with the response
          data: response.data 
        }))
      );
  
      const results = await Promise.all(promises);
      
      // Format events once when fetching
      const formattedEvents = results.flatMap((result) => 
        result.data.map(event => {
          const teacher = teachers.find(t => t.userid === result.teacherId); // Use the passed teacherId
          const isShared = event.is_shared || false;
  
          return {
            ...event,
            id: `event-${event.eventid}`,
            start: moment.utc(event.start).local().format(),
            end: moment.utc(event.end).local().format(),
            color: isShared ? '#005082' : (teacher ? teacher.color : '#444444'),
            backgroundColor: isShared 
              ? '#00508260' 
              : (teacher ? `${teacher.color}60` : '#44444480'),
            borderColor: isShared ? '#005082' : (teacher ? teacher.color : '#444444'),
            classNames: ['teacher-schedule-event']
          };
        })
      );
  
      setTeacherEvents(formattedEvents);
    } catch (error) {
      console.error('Error fetching teacher events:', error);
      setError('Failed to fetch events. Please try again later.');
    }
  }, [API_URL, teachers]);
  
  useEffect(() => {
    lastFetchedMonth.current = moment().format('YYYY-MM');
    fetchClasses();
    fetchClassChangeLogs();
    setTeacherEvents([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  

  useEffect(() => {
    const fetchTeachers = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/teachers`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
  
        // Filter teachers with status 'active'
        const activeTeachers = response.data.filter((teacher) => teacher.status === 'active');
  
        const teachersWithColors = activeTeachers.map((teacher) => ({
          ...teacher,
          color: teacher.color || '#000000',
        }));
  
        setTeachers(teachersWithColors);
  
        // Debugging: Log the teachers to verify colors
      } catch (error) {
        console.error('Error fetching teachers:', error);
      }
    };
  
    fetchTeachers();
  }, [API_URL]);
  

  useEffect(() => {
    const fetchClassrooms = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/classrooms`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
  
        const classroomsData = response.data || [];
  
        // Filter classrooms with availability 'true'
        const availableClassrooms = classroomsData.filter(classroom => classroom.availability === true);
  
        // Sort by 'number' field in alphanumeric order
        const sortedClassrooms = availableClassrooms.sort((a, b) => {
          return a.number.localeCompare(b.number, undefined, { numeric: true, sensitivity: 'base' });
        });
  
        setClassrooms(sortedClassrooms);
      } catch (error) {
        console.error('Error fetching classrooms:', error);
      }
    };
  
    fetchClassrooms();
  }, [API_URL]);
  

  useEffect(() => {
    if (selectedTeachers.length > 0) {
      fetchTeacherEvents(selectedTeachers);
    } else {
      setTeacherEvents([]);
    }
  }, [selectedTeachers, fetchTeacherEvents]);


  const handleClassroomChangeDropdown = (e) => {
    setEditedClassroomId(e.target.value);
  };

  const handleEditClassroomClick = (classId, classroomId) => {
    setEditingClassroomId(classId);
    setEditedClassroomId(classroomId); // Set initial classroom ID
  };

const handleSaveClassroomClick = async (classId) => {
  // Store current calendar state before update
  const calendarApi = calendarRef.current?.getApi();
  const currentView = calendarApi?.view.type;
  const currentDate = calendarApi?.getDate();

  try {
    const token = localStorage.getItem('token');
    const updatedClass = {
      classroom_id: editedClassroomId,
    };

    await axios.put(`${API_URL}/classes/${classId}`, updatedClass, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
    setEditingClassroomId(null);
    setEditedClassroomId('');
    
    // Refresh the class list
    await fetchClasses();
    
    // Restore calendar view after data is loaded
    setTimeout(() => {
      if (calendarApi && currentView && currentDate) {
        calendarApi.changeView(currentView, currentDate);
      }
    }, 100);
    
  } catch (error) {
    console.error('Error updating classroom:', error);
  }
};


  const checkOverlap = (newStart, newEnd, teacherId, eventId, allClasses, teacherEvents) => {
    return [...allClasses, ...teacherEvents].some(event => {
      // Skip the current event being moved/resized
      if (event.classid?.toString() === eventId || event.eventid?.toString() === eventId) return false;
      
      // Check all events for the same teacher
      if (event.teacher_id !== teacherId && event.userid !== teacherId) return false;
  
      const eventStart = moment(event.start || `${event.date} ${event.schedule_time}`);
      const eventEnd = moment(event.end || `${event.date} ${event.end_time}`);
  
      return (
        newStart.isBetween(eventStart, eventEnd, null, '[)') ||
        newEnd.isBetween(eventStart, eventEnd, null, '(]') ||
        (newStart.isSameOrBefore(eventStart) && newEnd.isSameOrAfter(eventEnd))
      );
    });
  };

  const handleEventDrop = (info) => {
    const eventId = info.event.id;
    const classItem = classes.find(cls => cls.classid.toString() === eventId);
  
    if (classItem && classItem.status === 'approved') {
      confirmAlert({
        title: t('Action Not Allowed'),
        message: t('You cannot change an approved class.'),
        buttons: [{ label: t('ok'), onClick: () => info.revert() }],
      });
      return;
    }
  
    const newStart = moment(info.event.start);
    const newEnd = moment(info.event.end);
    const teacherId = classItem?.teacher_id;
  
    const isOverlapping = checkOverlap(newStart, newEnd, teacherId, eventId, classes, teacherEvents);
  
    if (isOverlapping) {
      confirmAlert({
        title: t('Time Slot Conflict'),
        message: t('The selected time overlaps with an existing class or event for the same teacher. Please choose a different time.'),
        buttons: [{ label: t('Ok'), onClick: () => info.revert() }],
      });
      return;
    }
  
    setCurrentEventInfo(info);
    setShowModal(true);
  };
  
  const handleEventResize = (info) => {
    const eventId = info.event.id;
    const classItem = classes.find(cls => cls.classid.toString() === eventId);
  
    if (classItem && classItem.status === 'approved') {
      confirmAlert({
        title: t('Action Not Allowed'),
        message: t('You cannot change an approved class.'),
        buttons: [{ label: t('ok'), onClick: () => info.revert() }],
      });
      return;
    }
  
    const newStart = moment(info.event.start);
    const newEnd = moment(info.event.end);
    const teacherId = classItem?.teacher_id;
  
    const isOverlapping = checkOverlap(newStart, newEnd, teacherId, eventId, classes, teacherEvents);
  
    if (isOverlapping) {
      confirmAlert({
        title: t('Time Slot Conflict'),
        message: t('The selected time overlaps with an existing class or event for the same teacher. Please choose a different time.'),
        buttons: [{ label: t('Ok'), onClick: () => info.revert() }],
      });
      return;
    }
  
    setCurrentEventInfo(info);
    setShowModal(true);
  };

  const handleSubmitModal = async () => {
  const { event } = currentEventInfo;
  const classItem = classes.find(cls => cls.classid.toString() === event.id);

  // Store current calendar state before update
  const calendarApi = calendarRef.current?.getApi();
  const currentView = calendarApi?.view.type;
  const currentDate = calendarApi?.getDate();

  // Define original and new times
  const originalStartTime = classItem.schedule_time; 
  const originalEndTime = classItem.end_time; 
  const newStartTime = event.start.toTimeString().split(' ')[0].substring(0, 5);
  const newEndTime = event.end.toTimeString().split(' ')[0].substring(0, 5);

  // Format dates with moment-timezone
  const originalDate = moment.tz(classItem.date, 'Asia/Bangkok').format('YYYY-MM-DD');
  const newDate = moment.tz(event.start, 'Asia/Bangkok').format('YYYY-MM-DD');

  const updatedEvent = {
      id: event.id,
      date: newDate,
      schedule_time: newStartTime,
      schedule_hour: ((event.end - event.start) / 60000).toFixed(0)
  };

  try {
      const token = localStorage.getItem('token');
      
      await axios.put(`${API_URL}/classes/${updatedEvent.id}`, updatedEvent, {
          headers: {
              Authorization: `Bearer ${token}`
          }
      });

      await axios.post(`${API_URL}/class-change/class-change-logs`, {
          classid: updatedEvent.id,
          teacherid: classItem.teacher_id,
          originaldate: originalDate,
          originalstarttime: originalStartTime,
          originalendtime: originalEndTime,
          newdate: newDate,
          newstarttime: newStartTime,
          newendtime: newEndTime,
          changereason: changeReason,
          note: changeNote,
          updateby: token
      }, {
          headers: {
              Authorization: `Bearer ${token}`
          }
      });

      // Close modal first
      setShowModal(false);
      setCurrentEventInfo(null);
      setChangeReason('');
      setChangeNote('');
      
      // Refresh classes data
      await fetchClasses();
      await fetchClassChangeLogs();
      
      // Restore calendar view after data is loaded
      setTimeout(() => {
        if (calendarApi && currentView && currentDate) {
          calendarApi.changeView(currentView, currentDate);
        }
      }, 100);
      
  } catch (error) {
    console.error('Error updating event:', error);
    setShowModal(false);
    setCurrentEventInfo(null);
  }
};
  
const filteredClasses = classes
  .filter((classItem) => {
    const isSearchMatch = searchTerm === '' || 
      classItem.subject_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      classItem.class_code?.toLowerCase().includes(searchTerm.toLowerCase());
    const isTeacherMatch =
      selectedTeachers.length > 0
        ? selectedTeachers.includes(classItem.teacher_id)
        : true;
    return isSearchMatch && isTeacherMatch;
  })
  .sort((a, b) => {
    // First, compare dates
    const dateComparison = new Date(a.date) - new Date(b.date);
    if (dateComparison !== 0) {
      return dateComparison;
    }
    // If dates are the same, compare schedule times
    return moment(a.schedule_time, "HH:mm:ss").diff(
      moment(b.schedule_time, "HH:mm:ss")
    );
  });



// Separate future and past classes
const today = new Date().setHours(0, 0, 0, 0);
const futureClasses = filteredClasses.filter((classItem) => new Date(classItem.date) >= today);
const pastClasses = filteredClasses.filter((classItem) => new Date(classItem.date) < today);

// Combine and sort all classes, with older dates first
const allClasses = [...pastClasses, ...futureClasses].sort((a, b) => new Date(a.date) - new Date(b.date));

// Calculate total pages
const rowsPerPage = 55;
const pagesPerGroup = 10;
const totalPages = Math.ceil(allClasses.length / rowsPerPage);

// Set the initial page to where today or future classes start
useEffect(() => {
  const firstFutureIndex = pastClasses.length;
  const initialPage = Math.ceil((firstFutureIndex + 1) / rowsPerPage);
  setCurrentPage(initialPage > 1 ? initialPage : 1);
}, [pastClasses.length]);


// Pagination functions
const paginate = (pageNumber) => {
  setCurrentPage(pageNumber);
  setShowAll(false); // Reset showAll to false when navigating pages
};

const goToPreviousPageGroup = () => {
  if (currentPageGroup > 0) {
    setCurrentPageGroup(currentPageGroup - 1);
    setCurrentPage((currentPageGroup - 1) * pagesPerGroup + 1);
  }
};

const goToNextPageGroup = () => {
  if (endPage < totalPages) {
    setCurrentPageGroup(currentPageGroup + 1);
    setCurrentPage((currentPageGroup + 1) * pagesPerGroup + 1);
  }
};

// Calculate the start and end pages for the current group
const startPage = currentPageGroup * pagesPerGroup + 1;
const endPage = Math.min(startPage + pagesPerGroup - 1, totalPages);

// Create an array of page numbers to display
const pageNumbers = Array.from({ length: endPage - startPage + 1 }, (_, index) => startPage + index);
// Render current classes for the current page
const currentClasses = showAll
  ? allClasses
  : allClasses.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);


  const formatClassToEvent = (classItem) => {
    const startDate = new Date(classItem.date);
    const [startHours, startMinutes] = classItem.schedule_time.split(':').map(Number);

    if (isNaN(startHours) || isNaN(startMinutes)) {
        console.error('Invalid schedule time format:', classItem.schedule_time);
        return null;
    }

    startDate.setHours(startHours);
    startDate.setMinutes(startMinutes);
    startDate.setSeconds(0);
    startDate.setMilliseconds(0);

    if (isNaN(startDate.getTime())) {
        console.error('Invalid start date or time:', classItem.date, classItem.schedule_time, startDate);
        return null;
    }

    const totalMinutes = parseScheduleHour(classItem.schedule_hour || '');
    const endDateTime = new Date(startDate.getTime() + totalMinutes * 60000);

    if (isNaN(endDateTime.getTime())) {
        console.error('Invalid end date or time:', startDate, endDateTime, classItem.schedule_hour);
        return null;
    }

    const teacher = teachers.find((t) => t.userid === classItem.teacher_id);

    const titleLines = [
      `${classItem.class_code}`,
      `${classItem.subject_name} (${classItem.classid})`,
      `${classItem.nickname} / Room: ${classItem.classroom_number || '-'}`
  ];

    return {
        id: classItem.classid.toString(),  
        title: titleLines.join('<br />'), 
        start: startDate.toISOString(), 
        end: endDateTime.toISOString(),
        color: teacher ? teacher.color : '#333333', 
        backgroundColor: teacher ? teacher.color : '#333333',
    };
};

 const handleEventClick = (info) => {
    const eventId = info.event.id;
    
    // For class events (not teacher events)
    if (!eventId.includes('event-')) {
      const classItem = classes.find(cls => cls.classid.toString() === eventId);
      if (classItem) {
        const teacher = teachers.find(t => t.userid === classItem.teacher_id);
        setSelectedClass({
          classData: classItem,
          teacherData: teacher
        });
        setDetailsModalVisible(true);
      }
    } else {
      // For teacher events - you can handle these differently or ignore them
      const eventIdNum = eventId.replace('event-', '');
      const teacherEvent = teacherEvents.find(evt => evt.eventid?.toString() === eventIdNum);
      if (teacherEvent) {
        // You can choose to display teacher events in a different way or not show details
        console.log('Teacher event clicked', teacherEvent);
      }
    }
  };

const renderEventContent = (arg) => {
  const event = arg.event;
  const startTime = event.start
    ? moment(event.start).format('HH:mm')
    : '';
  const endTime = event.end
    ? moment(event.end).format('HH:mm')
    : '';
  
  return {
    html: `
      <div class="calendar-event teacher-event" style="
        padding: 4px;
        border-radius: 4px;
        opacity: 0.8;
        min-height: 100%;
        font-size: 0.85em;
      ">
        <div class="event-time">${startTime} - ${endTime}</div>
        <div class="event-title" style="white-space:pre-line">${event.title}</div>
      </div>
    `,
  };
};

const calendarEvents = [
  ...filteredClasses.map(formatClassToEvent).filter((event) => event !== null),
  ...teacherEvents // Use directly since they're already formatted
];

  if (loading) return <p>Loading...</p>;
  if (error) return <p>{error}</p>;

  const csvData = currentClasses.map((classItem) => ({
    Date: formatDate(classItem.date),
    'Schedule Day': getDayOfWeek(classItem.date),
    'Class Code': classItem.class_code,
    'Subject Name': classItem.subject_name,
    'Teacher Name': classItem.teacher_name,
    'Schedule Time': formatTime(classItem.schedule_time),
    'Schedule Hours': classItem.schedule_hour,
    Room: classItem.classroom_number,
    Type: classItem.isprivate ? 'Private' : 'Group',
  }));

  return (
    <div className="class-container">
      <Sidebar />
      <Header />

      <div className="top-filter-container">
      <div className="teacher-select-container">

           <div >
            <Input.Search
                placeholder={t('Search by class code or subject')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ width: '350px' }}
                allowClear
              />
          </div>
      <Select
  mode="multiple"
  showSearch
  style={{ width: '100%' }}
  placeholder={t('Select Teachers')}
  value={selectedTeachers}
  onChange={setSelectedTeachers}
  optionFilterProp="label" // Changed from children to label
  tagRender={tagRender}
  maxTagCount={3}
  maxTagPlaceholder={(omittedValues) => `+ ${omittedValues.length} ${t('more teachers')}`}
  filterOption={(input, option) => {
    // Check if the search term matches nickname or firstname
    return option?.label?.toLowerCase().includes(input.toLowerCase());
  }}
  dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
>
  {teachers.map(teacher => (
    <Option 
      key={teacher.userid} 
      value={teacher.userid}
      label={`${teacher.nickname} ${teacher.firstname}`} // Add label prop for search
      style={{
        padding: '8px 12px',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Space>
        <Tag color={teacher.color} style={{ margin: 0, width: 8, height: 8 }} />
        <span>{teacher.nickname} {teacher.firstname}</span>
      </Space>
    </Option>
  ))}
</Select>
      </div>
      </div>

    <div className="class-calendar-container">
     <FullCalendar
        ref={calendarRef}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        initialView={calendarView}
        initialDate={calendarDate}
        allDaySlot={false}
        locale={i18n.language === 'th' ? thLocale : 'en'}
        headerToolbar={{
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,timeGridWeek,timeGridDay',
        }}
        slotMinTime="08:00:00"
        slotMaxTime="23:00:00"
        slotDuration="00:15:00"
        snapDuration="00:15:00" 
        events={calendarEvents}
        eventClick={handleEventClick}
        editable={true}
        droppable={true}
        eventDrop={handleEventDrop}
        eventResize={handleEventResize}
        eventDisplay="block"
        eventBackgroundColor="transparent"
        eventContent={renderEventContent}
        eventClassNames={(info) => info.event.extendedProps.classNames}
        dayMaxEvents={0}
        slotLabelFormat={{
              hour: '2-digit',
              minute: '2-digit',
              hour12: false, 
            }}
        height="100%"
        datesSet={(dateInfo) => {
            if (!hasInitialized) {
              setHasInitialized(true);
              return;
            }

            const newMonth = moment(dateInfo.start).format('YYYY-MM');
            const prevMonth = lastFetchedMonth.current;

            setCalendarDate(dateInfo.start);
            setCalendarView(dateInfo.view.type);

            // Refetch when navigating to a different month
            if (newMonth !== prevMonth) {
              console.log('[AllClasses] Month changed:', prevMonth, '→', newMonth);
              lastFetchedMonth.current = newMonth;
              fetchClasses(dateInfo.start);

              if (selectedTeachers.length > 0) {
                fetchTeacherEvents(selectedTeachers);
              }
            }
          }}

        views={{
          dayGridMonth: {
            displayEventTime: true,
            displayEventEnd: true,
            dayMaxEvents: false
          },
          timeGridWeek: {
            displayEventTime: true,
            displayEventEnd: true
          },
          timeGridDay: {
            displayEventTime: true,
            displayEventEnd: true
          }
        }}
      />
       </div>

      <div className="classes-table-container">
        <div className="all-class-list">
          <h1>{t('allClassLists')}</h1>

          <div className="search-container">
            <input
              type="text"
              placeholder={t('searchBySubject')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
<div className="pagination-container">
  <button
    className={`page-button ${currentPageGroup === 0 ? 'disabled' : ''}`}
    onClick={goToPreviousPageGroup}
    disabled={currentPageGroup === 0}
  >
    {t('Prev 10 pages')}
  </button>
  <button
  className={`page-button ${currentPage === 1 ? 'disabled' : ''}`}
  onClick={() => currentPage > 1 && paginate(currentPage - 1)}
  disabled={currentPage === 1}
>
  <FontAwesomeIcon icon={faChevronLeft} /> 
</button>
  {pageNumbers.map((page) => (
    <button
      key={page}
      onClick={() => paginate(page)}
      className={`page-button ${currentPage === page ? 'active' : ''}`}
    >
      {page}
    </button>
  ))}
  <button
  className={`page-button ${currentPage === totalPages ? 'disabled' : ''}`}
  onClick={() => currentPage < totalPages && paginate(currentPage + 1)}
  disabled={currentPage === totalPages}
>
  <FontAwesomeIcon icon={faChevronRight} /> 
</button>
  <button
    className={`page-button ${endPage >= totalPages ? 'disabled' : ''}`}
    onClick={goToNextPageGroup}
    disabled={endPage >= totalPages}
  >
    {t('Next 10 pages')}
  </button>
  <CSVLink
    data={csvData}
    filename={`class_list_${new Date().toISOString()}.csv`}
    className="export-button"
  >
    <FontAwesomeIcon icon={faFileExcel} /> {t('exportCSV')}
  </CSVLink>
</div>

          <table className="classes-table">
            <thead>
              <tr>
                <th>{t('date')}</th>
                <th>{t('day')}</th>
                <th>{t('code')}</th>
                <th>{t('subject')}</th>
                <th>{t('tutor')}</th>
                <th>{t('start')}</th>
                <th>{t('end')}</th>
                <th>{t('hours')}</th>
                <th>{t('type')}</th>
                <th>{t('room')}</th>
                <th>{t('room')}</th>
                <th>{t('status')}</th>
                <th>{t('classid')}</th>
              </tr>
            </thead>
            <tbody>
              {currentClasses.map((classItem) => (
                <tr key={classItem.classid}>
                  <td>{formatDate(classItem.date)}</td>
                  <td>{getDayOfWeek(classItem.date)}</td>
                  <td>{classItem.class_code}</td>
                  <td>{classItem.subject_name}</td>
                  <td>{classItem.nickname}</td>
                  <td>{formatTime(classItem.schedule_time)}</td>
                  <td>{formatTime(classItem.end_time)}</td>
                  <td>{formatScheduleHour(classItem.schedule_hour)} {t('hr')}</td>
                  <td>{classItem.isprivate ? t('private') : t('group')}</td>
                  <td>{editingClassroomId === classItem.classid ? (
                    <select value={editedClassroomId} onChange={handleClassroomChangeDropdown}>
                      <option value="">{t('selectClassroom')}</option>
                      {classrooms.map((classroom) => (
                        <option key={classroom.classroomid} value={classroom.classroomid}>
                          {classroom.number}
                        </option>
                      ))}
                    </select>
                  ) : classItem.classroom_number}</td>
                  <td><div>
                    {editingClassroomId === classItem.classid ? (
                      <button onClick={() => handleSaveClassroomClick(classItem.classid)} className="save-button"><FontAwesomeIcon icon={faSave} /></button>
                    ) : (
                      <button onClick={() => handleEditClassroomClick(classItem.classid, classItem.classroom_id)} className="edit-button"><FontAwesomeIcon icon={faChalkboard} /></button>
                    )}
                  </div></td>             
                  <td>{classItem.status}</td>
                  <td>{classItem.classid}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="all-class-card-list">
          {currentClasses.map((classItem) => (
            <div key={classItem.classid} className="all-class-card">
              <div className="all-class-card-header">
                {classItem.class_code} - {classItem.subject_name}
              </div>
              <div className="all-class-card-body">
                <div className="class-info">
                  <label>{t('date')}: </label>{formatDate(classItem.date)}
                </div>
                <div className="class-info">
                  <label>{t('day')}: </label>{getDayOfWeek(classItem.date)}
                </div>
                <div className="class-info">
                  <label>{t('tutor')}: </label>{classItem.teacher_name}
                </div>
                <div className="class-info">
                  <label>{t('start')}: </label>{formatTime(classItem.schedule_time)}
                </div>
                <div className="class-info">
                  <label>{t('end')}: </label>{formatTime(classItem.end_time)}
                </div>
                <div className="class-info">
                  <label>{t('hours')}: </label>{formatScheduleHour(classItem.schedule_hour)}
                </div>
                <div className="class-info">
                  <label>{t('room')}: </label>{classItem.classroom_number}
                </div>
                <div className="class-info">
                  <button className="edit-button" onClick={() => handleEditClassroomClick(classItem.classid, classItem.classroom_id)}>
                    <FontAwesomeIcon icon={faChalkboard} /> {t('edit')}
                  </button>
                </div>
              </div>
            </div>
             ))}
             </div>
        </div>
    </div>

        <ClassDetailsModal
          visible={detailsModalVisible}
          onClose={() => setDetailsModalVisible(false)}
          classData={selectedClass?.classData}
          teacherData={selectedClass?.teacherData}
          classChangeLogs={classChangeLogs}
        />
      <CustomModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={handleSubmitModal}
        reason={changeReason}
        setReason={setChangeReason}
        note={changeNote}
        setNote={setChangeNote}
      />
    </div>
  );
}

export default withRoleAccess(AllClasses, ['admin', 'superadmin']);